import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Building, Bot, CreditCard, Save, Power, Trash2, AlertTriangle, Plus, X, Phone, Check, Zap, Minus, ChevronDown, ShieldCheck, Monitor, LogOut, MapPin, Calendar, Loader2, Link2 } from 'lucide-react';
import LocationsTab from './LocationsTab';

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'business', label: 'Business', icon: Building },
  { id: 'locations', label: 'Locations', icon: MapPin },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DEFAULT_HOURS = DAYS.map((day, i) => ({
  day, open: i < 5 ? '09:00' : i === 5 ? '09:00' : '', close: i < 5 ? '18:00' : i === 5 ? '15:00' : '', enabled: i < 6
}));

export default function SettingsPage() {
  const { user, api, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const business = user?.business;
  // Read ?tab=... from URL so /dashboard/settings?tab=billing opens Billing
  const initialTab = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab')) || 'profile';
  const [tab, setTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [errMsg, setErrMsg] = useState('');

  // Profile state
  const [profile, setProfile] = useState({ name: user?.name || '', email: user?.email || '' });
  const [profilePwd, setProfilePwd] = useState('');         // current password (only required for email changes)
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new_pass: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);

  // 2FA state
  const [twoFA, setTwoFA] = useState({ enabled: !!user?.two_factor_enabled, password: '', busy: false });
  useEffect(() => { setTwoFA(s => ({ ...s, enabled: !!user?.two_factor_enabled })); }, [user?.two_factor_enabled]);

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsBusyId, setSessionsBusyId] = useState('');

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const { data } = await api.get('/auth/sessions');
      setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (e) {
      // Silent — sessions panel just won't render anything if it fails.
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => { if (tab === 'profile') { loadSessions(); } /* eslint-disable-next-line */ }, [tab]);

  const handleRevokeSession = async (sid) => {
    if (!sid) return;
    setSessionsBusyId(sid);
    try {
      await api.post(`/auth/sessions/${sid}/revoke`);
      showSaved('Session revoked.');
      await loadSessions();
    } catch (e) {
      showError(e.response?.data?.detail || 'Could not revoke that session.');
    } finally {
      setSessionsBusyId('');
    }
  };

  const handleRevokeAllOthers = async () => {
    setSessionsBusyId('__all__');
    try {
      const { data } = await api.post('/auth/sessions/revoke-all-others');
      const n = data?.revoked_count ?? 0;
      showSaved(n > 0 ? `Signed out ${n} other session${n === 1 ? '' : 's'}.` : 'No other sessions to sign out.');
      await loadSessions();
    } catch (e) {
      showError(e.response?.data?.detail || 'Could not sign out other sessions.');
    } finally {
      setSessionsBusyId('');
    }
  };

  // Business state
  const [biz, setBiz] = useState({
    name: business?.name || '', niche: business?.niche || 'other', phone: business?.phone || '',
    email: business?.email || '', address: business?.address || '', website: business?.website || '',
    timezone: business?.timezone || 'America/Vancouver',
  });
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [services, setServices] = useState([]);

  // AI Agent state
  const [agent, setAgent] = useState({
    name: business?.ai_agent_name || 'Ava',
    voice: 'Clara',
    firstMessage: business?.first_message || `Hi, thank you for calling ${business?.name || 'our business'}! This is Ava. How can I help you today?`,
    systemPrompt: business?.system_prompt || '',
    knowledgeBase: business?.knowledge_base || '',
    faqs: Array.isArray(business?.faqs) ? business.faqs : [],
    language: 'en',
    active: business?.is_active !== false,
  });
  const [savingAgent, setSavingAgent] = useState(false);
  const [newFaqQ, setNewFaqQ] = useState('');
  const [newFaqA, setNewFaqA] = useState('');

  const handleSaveAgent = async () => {
    setSavingAgent(true);
    try {
      await api.put('/business/me', {
        ai_agent_name: agent.name,
        first_message: agent.firstMessage,
        system_prompt: agent.systemPrompt,
        knowledge_base: agent.knowledgeBase,
        faqs: agent.faqs,
      });
      showSaved('AI Agent updated, Ava will use this on her next call.');
    } catch (e) {
      showSaved('Save failed, please try again.');
    } finally {
      setSavingAgent(false);
    }
  };

  const addFaq = () => {
    const q = newFaqQ.trim(), a = newFaqA.trim();
    if (!q || !a) return;
    setAgent(prev => ({ ...prev, faqs: [...prev.faqs, { q, a }] }));
    setNewFaqQ(''); setNewFaqA('');
  };
  const removeFaq = (i) => setAgent(prev => ({ ...prev, faqs: prev.faqs.filter((_, idx) => idx !== i) }));

  const showSaved = (msg) => { setErrMsg(''); setSaved(msg); setTimeout(() => setSaved(''), 3000); };
  const showError = (msg) => { setSaved(''); setErrMsg(msg); setTimeout(() => setErrMsg(''), 4000); };

  const handleSaveProfile = async () => {
    const trimmedName = (profile.name || '').trim();
    const trimmedEmail = (profile.email || '').trim().toLowerCase();
    const emailChanging = trimmedEmail && trimmedEmail !== (user?.email || '').toLowerCase();
    if (emailChanging && !profilePwd) {
      showError('Enter your current password to change your email.');
      return;
    }
    setSavingProfile(true);
    try {
      const payload = { name: trimmedName, email: trimmedEmail };
      if (emailChanging) payload.current_password = profilePwd;
      await api.put('/auth/profile', payload);
      await refreshUser();
      setProfilePwd('');
      showSaved('Profile updated.');
    } catch (e) {
      showError(e.response?.data?.detail || 'Could not update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwords.current || !passwords.new_pass) {
      showError('Enter your current and new password.');
      return;
    }
    if (passwords.new_pass.length < 8) {
      showError('New password must be at least 8 characters.');
      return;
    }
    if (passwords.new_pass !== passwords.confirm) {
      showError("New password and confirmation don't match.");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwords.current,
        new_password: passwords.new_pass,
      });
      setPasswords({ current: '', new_pass: '', confirm: '' });
      showSaved('Password updated.');
    } catch (e) {
      showError(e.response?.data?.detail || 'Could not update password.');
    } finally {
      setSavingPwd(false);
    }
  };

  const handleToggle2FA = async (nextEnabled) => {
    if (!twoFA.password) {
      showError('Enter your current password to change two-factor authentication.');
      return;
    }
    setTwoFA(s => ({ ...s, busy: true }));
    try {
      const { data } = await api.post('/auth/2fa-toggle', {
        enabled: !!nextEnabled,
        current_password: twoFA.password,
      });
      setTwoFA({ enabled: !!data.two_factor_enabled, password: '', busy: false });
      await refreshUser();
      showSaved(data.message || (nextEnabled ? 'Two-factor authentication enabled.' : 'Two-factor authentication disabled.'));
      // Tell the dashboard reminder it can disappear (or come back).
      try { window.dispatchEvent(new Event('nextbell:2fa-changed')); } catch { /* noop */ }
    } catch (e) {
      setTwoFA(s => ({ ...s, busy: false }));
      showError(e.response?.data?.detail || 'Could not change two-factor authentication.');
    }
  };

  const handleSaveBusiness = async () => {
    setSaving(true);
    try { await api.put('/business/me', biz); showSaved('Business info saved.'); } catch {} finally { setSaving(false); }
  };

  const handleToggleAI = async () => {
    const newState = !agent.active;
    setAgent({ ...agent, active: newState });
    try { await api.put('/business/me', { is_active: newState }); showSaved(newState ? 'AI Agent activated.' : 'AI Agent stopped.'); } catch { setAgent({ ...agent, active: !newState }); }
  };

  const handleDeactivate = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div data-testid="settings-page" className="space-y-6">
      <h1 className="font-heading text-2xl font-semibold text-gray-900 dark:text-white">Settings</h1>
      {saved && <div className="bg-green-50 dark:bg-green-500/15 border border-green-200 dark:border-green-500/30 rounded-xl px-4 py-3 text-green-700 dark:text-green-300 text-sm" data-testid="settings-saved">{saved}</div>}
      {errMsg && <div className="bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-xl px-4 py-3 text-red-700 dark:text-red-300 text-sm" data-testid="settings-error">{errMsg}</div>}

      {/* Tabs — mobile uses a native select, desktop uses pill bar (no horizontal scroll) */}
      <div className="sm:hidden">
        <div className="relative">
          <select
            data-testid="settings-tab-mobile"
            value={tab}
            onChange={(e) => setTab(e.target.value)}
            className="w-full appearance-none bg-white dark:bg-white/[0.06] border border-gray-200 dark:border-white/10 rounded-xl pl-4 pr-10 py-3 text-sm font-medium text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            {TABS.map(t => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 pointer-events-none" />
        </div>
      </div>
      <div className="hidden sm:flex flex-wrap gap-1 bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl p-1">
        {TABS.map(t => (
          <button key={t.id} data-testid={`settings-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/[0.05]'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {/* Tab 1: Profile */}
      {tab === 'profile' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-5 sm:p-6">
            <h2 className="font-heading text-lg font-medium text-gray-900 dark:text-white mb-4">Your Profile</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Full Name</label>
                <input data-testid="profile-name-input" type="text" value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})}
                  className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" /></div>
              <div><label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Email</label>
                <input data-testid="profile-email-input" type="email" value={profile.email} onChange={e => setProfile({...profile, email: e.target.value})}
                  className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" /></div>
              {profile.email && profile.email.toLowerCase() !== (user?.email || '').toLowerCase() && (
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Current password <span className="text-gray-400 dark:text-slate-500">(required to change email)</span></label>
                  <input data-testid="profile-current-pwd-input" type="password" value={profilePwd} onChange={e => setProfilePwd(e.target.value)} placeholder="Enter your current password"
                    className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                </div>
              )}
            </div>
            <button data-testid="save-profile-btn" onClick={handleSaveProfile} disabled={savingProfile} className="mt-4 bg-blue-600 hover:bg-blue-500 dark:hover:bg-blue-400 disabled:opacity-50 text-white text-sm font-medium rounded-full px-5 py-2 flex items-center gap-1.5">
              <Save className="w-3.5 h-3.5" /> {savingProfile ? 'Saving…' : 'Save Profile'}
            </button>
          </div>
          <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-5 sm:p-6">
            <h2 className="font-heading text-lg font-medium text-gray-900 dark:text-white mb-4">Change Password</h2>
            <div className="space-y-3 max-w-md">
              <input data-testid="pwd-current-input" type="password" placeholder="Current password" value={passwords.current} onChange={e => setPasswords({...passwords, current: e.target.value})}
                className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500" />
              <input data-testid="pwd-new-input" type="password" placeholder="New password (min 8 characters)" value={passwords.new_pass} onChange={e => setPasswords({...passwords, new_pass: e.target.value})}
                className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500" />
              <input data-testid="pwd-confirm-input" type="password" placeholder="Confirm new password" value={passwords.confirm} onChange={e => setPasswords({...passwords, confirm: e.target.value})}
                className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500" />
            </div>
            <button data-testid="update-password-btn" onClick={handleChangePassword} disabled={savingPwd} className="mt-4 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white text-sm font-medium rounded-full px-5 py-2">
              {savingPwd ? 'Updating…' : 'Update Password'}
            </button>
          </div>

          {/* 2FA toggle */}
          <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${twoFA.enabled ? 'bg-emerald-50 dark:bg-emerald-500/15' : 'bg-gray-100 dark:bg-white/[0.05]'}`}>
                <ShieldCheck className={`w-5 h-5 ${twoFA.enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-slate-500'}`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-heading text-lg font-medium text-gray-900 dark:text-white">Two-factor authentication</h2>
                <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">When enabled, we'll email you a 6-digit code on every login. Strongly recommended.</p>
              </div>
              <span className={`text-[10px] uppercase tracking-wide font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${twoFA.enabled ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-slate-400'}`}>
                {twoFA.enabled ? 'On' : 'Off'}
              </span>
            </div>
            <input data-testid="2fa-current-pwd-input" type="password" placeholder="Current password" value={twoFA.password} onChange={e => setTwoFA(s => ({ ...s, password: e.target.value }))}
              className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full max-w-md border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500" />
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              {!twoFA.enabled ? (
                <button data-testid="enable-2fa-btn" onClick={() => handleToggle2FA(true)} disabled={twoFA.busy || !twoFA.password}
                  className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded-full px-5 py-2 flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-4 h-4" /> {twoFA.busy ? 'Enabling…' : 'Enable two-factor authentication'}
                </button>
              ) : (
                <button data-testid="disable-2fa-btn" onClick={() => handleToggle2FA(false)} disabled={twoFA.busy || !twoFA.password}
                  className="bg-red-50 dark:bg-red-500/15 hover:bg-red-100 dark:hover:bg-red-500/25 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30 disabled:opacity-50 text-sm font-medium rounded-full px-5 py-2 flex items-center justify-center gap-1.5">
                  <X className="w-4 h-4" /> {twoFA.busy ? 'Disabling…' : 'Disable two-factor authentication'}
                </button>
              )}
            </div>
          </div>

          {/* Active sessions */}
          <div data-testid="sessions-panel" className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-50 dark:bg-blue-500/15">
                  <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-heading text-lg font-medium text-gray-900 dark:text-white">Active sessions</h2>
                  <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">Devices and browsers currently signed into your account.</p>
                </div>
              </div>
              {sessions.length > 1 && (
                <button
                  data-testid="revoke-all-others-btn"
                  onClick={handleRevokeAllOthers}
                  disabled={sessionsBusyId === '__all__'}
                  className="bg-red-50 dark:bg-red-500/15 hover:bg-red-100 dark:hover:bg-red-500/25 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30 disabled:opacity-50 text-xs sm:text-sm font-medium rounded-full px-3 sm:px-4 py-1.5 flex items-center gap-1.5 flex-shrink-0"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  {sessionsBusyId === '__all__' ? 'Signing out…' : 'Sign out everywhere else'}
                </button>
              )}
            </div>

            {sessionsLoading && sessions.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-slate-400 py-4">Loading sessions…</div>
            ) : sessions.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-slate-400 py-4">No active sessions found.</div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]" data-testid="sessions-list">
                {sessions.map((s) => {
                  const lastSeen = s.last_seen_at ? new Date(s.last_seen_at) : null;
                  const created = s.created_at ? new Date(s.created_at) : null;
                  const fmt = (d) => d ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-';
                  return (
                    <li key={s.id} data-testid={`session-row-${s.id}`} className="py-3 sm:py-4 flex items-start sm:items-center gap-3 sm:gap-4 flex-col sm:flex-row">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{s.device || 'Unknown device'}</span>
                          {s.is_current && (
                            <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                              This device
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-slate-400 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span data-testid={`session-ip-${s.id}`}>IP {s.ip || 'unknown'}</span>
                          <span>Last active {fmt(lastSeen)}</span>
                          <span>Started {fmt(created)}</span>
                        </div>
                      </div>
                      {!s.is_current && (
                        <button
                          data-testid={`revoke-session-btn-${s.id}`}
                          onClick={() => handleRevokeSession(s.id)}
                          disabled={sessionsBusyId === s.id}
                          className="bg-white dark:bg-white/[0.04] hover:bg-gray-50 dark:hover:bg-white/[0.08] text-gray-700 dark:text-slate-200 border border-gray-200 dark:border-white/10 disabled:opacity-50 text-xs font-medium rounded-full px-3 py-1.5 flex items-center gap-1.5 flex-shrink-0 self-end sm:self-auto"
                        >
                          <X className="w-3.5 h-3.5" />
                          {sessionsBusyId === s.id ? 'Revoking…' : 'Revoke'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
      {tab === 'business' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-medium text-gray-900 dark:text-white">Business Information</h2>
              <button onClick={handleSaveBusiness} disabled={saving} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 dark:hover:bg-blue-400 text-white text-sm font-medium rounded-full px-4 py-2 disabled:opacity-50">
                <Save className="w-3.5 h-3.5" />{saving ? 'Saving...' : 'Save'}
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Business Name</label>
                <input type="text" value={biz.name} onChange={e => setBiz({...biz, name: e.target.value})} className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" /></div>
              <div><label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Niche / Type</label>
                <select value={biz.niche} onChange={e => setBiz({...biz, niche: e.target.value})} className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:border-blue-500">
                  <option value="hair_salon">Hair Salon</option><option value="spa">Spa</option><option value="dental">Dental</option>
                  <option value="hvac">HVAC</option><option value="law">Law Firm</option><option value="real_estate">Real Estate</option>
                  <option value="vet">Vet Clinic</option><option value="other">Other</option>
                </select></div>
              <div><label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Phone</label>
                <input type="tel" value={biz.phone} onChange={e => setBiz({...biz, phone: e.target.value})} className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" /></div>
              <div><label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Business Email</label>
                <input type="email" value={biz.email} onChange={e => setBiz({...biz, email: e.target.value})} className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" /></div>
              <div><label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Address</label>
                <input type="text" value={biz.address} onChange={e => setBiz({...biz, address: e.target.value})} className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" /></div>
              <div><label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Timezone</label>
                <select value={biz.timezone} onChange={e => setBiz({...biz, timezone: e.target.value})} className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:border-blue-500">
                  <option value="America/Vancouver">Pacific (Vancouver)</option><option value="America/Denver">Mountain</option>
                  <option value="America/Chicago">Central</option><option value="America/New_York">Eastern</option><option value="America/Toronto">Toronto</option>
                </select></div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-5 sm:p-6">
            <h2 className="font-heading text-lg font-medium text-gray-900 dark:text-white mb-4">Business Hours</h2>
            <div className="space-y-3">
              {hours.map((h, i) => (
                <div key={h.day} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 bg-gray-50 dark:bg-white/[0.03] sm:bg-transparent dark:sm:bg-transparent rounded-xl sm:rounded-none p-3 sm:p-0 border border-gray-100 dark:border-white/[0.06] sm:border-0">
                  {/* Day + toggle row */}
                  <div className="flex items-center gap-3 sm:gap-4 sm:w-44 sm:flex-shrink-0">
                    <button onClick={() => { const u = [...hours]; u[i].enabled = !u[i].enabled; setHours(u); }}
                      aria-label={`Toggle ${h.day}`}
                      className={`w-10 h-5 rounded-full relative transition-colors flex-shrink-0 ${h.enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-white/10'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${h.enabled ? 'left-5' : 'left-0.5'}`} />
                    </button>
                    <span className="text-sm font-medium text-gray-700 dark:text-slate-200">{h.day}</span>
                    {!h.enabled && <span className="ml-auto sm:ml-0 text-xs text-gray-400 dark:text-slate-500">Closed</span>}
                  </div>
                  {/* Time row — hidden when disabled on mobile, dimmed on desktop */}
                  {h.enabled && (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <input type="time" value={h.open} onChange={e => { const u = [...hours]; u[i].open = e.target.value; setHours(u); }}
                        className="flex-1 sm:flex-initial sm:w-32 border border-gray-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none" />
                      <span className="text-gray-400 dark:text-slate-500 text-xs">to</span>
                      <input type="time" value={h.close} onChange={e => { const u = [...hours]; u[i].close = e.target.value; setHours(u); }}
                        className="flex-1 sm:flex-initial sm:w-32 border border-gray-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button className="mt-4 bg-blue-600 hover:bg-blue-500 dark:hover:bg-blue-400 text-white text-sm font-medium rounded-full px-5 py-2">Save Hours</button>
          </div>

          {/* Services */}
          <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2 className="font-heading text-lg font-medium text-gray-900 dark:text-white">Services</h2>
              <button onClick={() => setServices([...services, { name: '', price: '', duration: '30', desc: '' }])}
                className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 font-medium flex-shrink-0"><Plus className="w-4 h-4" /> Add</button>
            </div>
            {services.length === 0 ? (
              <p className="text-gray-400 dark:text-slate-500 text-sm">No services added yet. Add your services and they'll sync to your AI agent and website.</p>
            ) : (
              <div className="space-y-3">
                {services.map((s, i) => (
                  <div key={i} className="bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] rounded-xl p-3 space-y-2">
                    {/* Service name — full width */}
                    <input type="text" placeholder="Service name (e.g., Haircut)" value={s.name} onChange={e => { const u = [...services]; u[i].name = e.target.value; setServices(u); }}
                      className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
                    {/* Price + duration + delete row */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm pointer-events-none">$</span>
                        <input type="number" placeholder="Price" value={s.price} onChange={e => { const u = [...services]; u[i].price = e.target.value; setServices(u); }}
                          className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg pl-7 pr-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
                      </div>
                      <div className="flex-1 relative">
                        <input type="number" placeholder="Min" value={s.duration} onChange={e => { const u = [...services]; u[i].duration = e.target.value; setServices(u); }}
                          className="bg-white dark:bg-white/[0.05] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs pointer-events-none">min</span>
                      </div>
                      <button onClick={() => setServices(services.filter((_, j) => j !== i))} aria-label="Remove service"
                        className="w-9 h-9 flex-shrink-0 rounded-lg bg-red-50 dark:bg-red-500/15 hover:bg-red-100 dark:hover:bg-red-500/25 text-red-600 dark:text-red-400 flex items-center justify-center transition-colors"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'locations' && <LocationsTab api={api} />}

      {tab === 'calendar' && <CalendarTab api={api} />}

      {/* Tab 5: Billing */}
      {tab === 'billing' && <BillingTab api={api} business={business} />}
    </div>
  );
}

function CalendarTab({ api }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/calendar/integration');
      setStatus(data);
    } catch { /* leave disconnected */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const flash = (ok, text) => { setErr(ok ? '' : text); setMsg(ok ? text : ''); setTimeout(() => { setMsg(''); setErr(''); }, 4000); };

  const connect = async () => {
    if (!apiKey.trim()) return;
    setBusy('connect'); setErr(''); setMsg('');
    try {
      const { data } = await api.post('/calendar/connect', { api_key: apiKey.trim() });
      setStatus(data); setApiKey('');
      flash(true, 'Cal.com connected.');
    } catch (e) { flash(false, e?.response?.data?.detail || 'Could not connect.'); }
    finally { setBusy(''); }
  };
  const test = async () => {
    setBusy('test');
    try { await api.post('/calendar/test'); flash(true, 'Connection works.'); await load(); }
    catch (e) { flash(false, e?.response?.data?.detail || 'Test failed.'); }
    finally { setBusy(''); }
  };
  const disconnect = async () => {
    setBusy('disconnect');
    try { const { data } = await api.post('/calendar/disconnect'); setStatus(data); flash(true, 'Disconnected.'); }
    catch (e) { flash(false, e?.response?.data?.detail || 'Could not disconnect.'); }
    finally { setBusy(''); }
  };
  const pickEventType = async (id) => {
    setBusy('et');
    try { const { data } = await api.post('/calendar/event-type', { event_type_id: Number(id) }); setStatus(data); flash(true, 'Saved.'); }
    catch (e) { flash(false, e?.response?.data?.detail || 'Could not save.'); }
    finally { setBusy(''); }
  };

  if (loading) return <div className="flex justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const connected = status?.connected;
  const eventTypes = status?.event_types || [];

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Calendar</h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
          Connect Cal.com so every appointment Ava books lands on your real calendar automatically.
        </p>
      </div>

      {msg && <div className="rounded-xl px-4 py-3 text-sm bg-green-50 dark:bg-green-500/15 border border-green-200 dark:border-green-500/30 text-green-700 dark:text-green-300">{msg}</div>}
      {err && <div className="rounded-xl px-4 py-3 text-sm bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300">{err}</div>}

      {!connected ? (
        <div className="rounded-2xl p-6 bg-white border border-gray-200 dark:bg-white/[0.04] dark:border-white/10 space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-200">
            <Link2 className="w-4 h-4 text-blue-500 dark:text-blue-400" /> Connect with a Cal.com API key
          </div>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            In Cal.com go to Settings → Developer → API Keys, create a key, and paste it here.
          </p>
          <input
            type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
            placeholder="cal_live_..." autoComplete="off"
            className="w-full rounded-lg border border-gray-200 dark:border-white/10 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:border-blue-400"
          />
          <button onClick={connect} disabled={busy === 'connect' || !apiKey.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5">
            {busy === 'connect' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Connect Cal.com
          </button>
        </div>
      ) : (
        <div className="rounded-2xl p-6 bg-white border border-gray-200 dark:bg-white/[0.04] dark:border-white/10 space-y-5">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
              <Check className="w-4 h-4" /> Cal.com connected
            </span>
            <button onClick={disconnect} disabled={busy === 'disconnect'} className="text-xs text-red-500 hover:underline">
              {busy === 'disconnect' ? 'Removing…' : 'Disconnect'}
            </button>
          </div>

          <div>
            <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1.5">Book into this event type</label>
            <select
              value={status?.default_event_type_id || ''}
              onChange={e => pickEventType(e.target.value)} disabled={busy === 'et'}
              className="w-full rounded-lg border border-gray-200 dark:border-white/10 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none">
              <option value="" className="dark:bg-[#0a0a0a]">Select an event type…</option>
              {eventTypes.map(et => (
                <option key={et.id} value={et.id} className="dark:bg-[#0a0a0a]">
                  {et.title}{et.length ? ` (${et.length} min)` : ''}
                </option>
              ))}
            </select>
            {!status?.default_event_type_id && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">Pick an event type so Ava knows where to book.</p>
            )}
          </div>

          <button onClick={test} disabled={busy === 'test'}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-white/10 hover:border-blue-400 text-sm text-gray-700 dark:text-slate-200 px-4 py-2">
            {busy === 'test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Test connection
          </button>
        </div>
      )}
    </div>
  );
}

function BillingTab({ api, business }) {
  const [status, setStatus] = useState(null);
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refundEligible, setRefundEligible] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundDaysLeft, setRefundDaysLeft] = useState(0);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundResult, setRefundResult] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/billing/status'); setStatus(data); } catch {}
      try {
        const { data } = await api.get('/billing/refund-eligibility');
        setRefundEligible(!!data.eligible);
        setRefundReason(data.reason || '');
        setRefundDaysLeft(Number(data.days_left || 0));
      } catch {}
    })();
  }, [api]);

  const startCheckout = async (plan) => {
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/billing/checkout', { plan, billing });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/billing/portal');
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Portal unavailable. Subscribe to a plan first.');
      setLoading(false);
    }
  };

  const requestRefund = async () => {
    setError(''); setRefundResult(''); setRefundLoading(true);
    try {
      const { data } = await api.post('/billing/refund-request');
      setRefundResult(`Refund of $${(data.amount_cents / 100).toFixed(2)} issued. It usually arrives in 5 to 10 business days.`);
      setRefundEligible(false);
      setShowConfirm(false);
      // Refresh status so the UI shows refunded state
      try { const r = await api.get('/billing/status'); setStatus(r.data); } catch {}
    } catch (e) {
      setError(e.response?.data?.detail || 'Refund failed. Please contact support.');
    } finally {
      setRefundLoading(false);
    }
  };

  const isTrial = status?.is_trial !== false && (business?.is_trial || status?.is_trial);
  const currentPlan = status?.plan || business?.plan || 'starter';
  const minUsed = business?.minutes_used || 0;
  const minCap = business?.minutes_cap || 30;
  const smsUsed = business?.sms_used || 0;
  const smsCap = business?.sms_cap || 10;
  const hasStripeCustomer = status?.stripe_customer_id_present;

  // Always display the current public pricing in the dashboard so it matches
  // the marketing site. Backend grandfathering (caps_for_business +
  // is_legacy_pricing) still preserves existing customers' caps under the hood.
  const plans = [
    { id: 'starter', name: 'Starter', monthly: 179.99, yearly: 1800, minutes: 250, sms: 100, features: ['AI Receptionist (Ava)', 'CRM & Lead Pipeline', 'Booking Calendar', 'Call Logs & Transcripts', 'Overage at $0.45/min'] },
    { id: 'growth', name: 'Growth', monthly: 279.99, yearly: 2800, minutes: 500, sms: 250, features: ['Everything in Starter', 'SMS Follow-ups & Reminders', 'Analytics Dashboard', 'Calendar Sync', 'Priority Support'], popular: true },
    { id: 'agency', name: 'Agency · White-label', monthly: 1497, yearly: 14970, minutes: 2000, sms: 1000, features: ['Everything in Growth', 'Multi-location support', 'White-label branding', 'Priority support'] },
  ];

  return (
    <div className="space-y-6" data-testid="billing-tab">
      {/* Trial banner */}
      {isTrial && (
        <div className="bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/40 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">You're on the 7-day free trial</p>
              <p className="text-xs text-amber-700 dark:text-amber-300/90 mt-1">
                {status?.trial_ends_at && `Trial ends ${new Date(status.trial_ends_at).toLocaleDateString()}. `}
                You have {Math.max(0, minCap - minUsed)} of {minCap} free minutes left. Pick a plan below to keep Ava running.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current usage */}
      <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wide">Current plan</p>
            <p className="text-2xl font-heading font-semibold text-gray-900 dark:text-white capitalize">{isTrial ? `Free Trial (${currentPlan})` : currentPlan}</p>
            {status?.subscription_status && !isTrial && <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Status: {status.subscription_status}</p>}
          </div>
          {hasStripeCustomer && (
            <button onClick={openPortal} disabled={loading} data-testid="manage-billing-btn"
              className="bg-gray-100 dark:bg-white/[0.05] hover:bg-gray-200 disabled:opacity-50 text-gray-700 dark:text-slate-200 text-sm font-medium rounded-full px-4 py-2">
              Manage billing
            </button>
          )}
        </div>
        <div className="space-y-3">
          <Usage label="Minutes" used={minUsed} cap={minCap} />
          <Usage label="SMS" used={smsUsed} cap={smsCap} />
        </div>
      </div>

      {/* Pick a plan */}
      <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h2 className="font-heading text-lg font-medium text-gray-900 dark:text-white">{isTrial ? 'Subscribe' : 'Change plan'}</h2>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-white/[0.05] rounded-full p-0.5">
              <button onClick={() => setBilling('monthly')} className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all ${billing === 'monthly' ? 'bg-white dark:bg-white/15 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-slate-400'}`} data-testid="bill-monthly">Monthly</button>
              <button onClick={() => setBilling('yearly')} className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all ${billing === 'yearly' ? 'bg-white dark:bg-white/15 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-slate-400'}`} data-testid="bill-yearly">Yearly</button>
            </div>
            {billing === 'yearly' && <span data-testid="bill-save-badge" className="text-xs bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/40 px-2 py-0.5 rounded-full font-medium">2 months free</span>}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map(p => {
            const isCurrent = p.id === currentPlan && !isTrial;
            const monthlyEquiv = billing === 'yearly' ? Math.round(p.yearly / 12) : p.monthly;
            const yearlySavings = (p.monthly * 12) - p.yearly;
            return (
              <div key={p.id} className={`border rounded-2xl p-5 ${p.popular ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 dark:border-white/10'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-medium text-gray-900 dark:text-white">{p.name}</h3>
                  {p.popular && <span className="text-[10px] uppercase font-medium tracking-wide bg-blue-100 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">Most popular</span>}
                </div>
                <p className="text-3xl font-heading font-semibold text-gray-900 dark:text-white mb-1">
                  ${monthlyEquiv}<span className="text-sm font-normal text-gray-400 dark:text-slate-500">/mo</span>
                </p>
                {billing === 'yearly' ? (
                  <p className="text-[11px] text-blue-600 dark:text-blue-400 font-medium mb-3">
                    Billed ${p.yearly.toLocaleString()}/yr, save ${yearlySavings.toLocaleString()}
                  </p>
                ) : (
                  <p className="text-[11px] text-gray-400 dark:text-slate-500 mb-3">Billed monthly</p>
                )}
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-4">{p.minutes.toLocaleString()} min · {p.sms.toLocaleString()} SMS / mo</p>
                <ul className="text-xs text-gray-600 dark:text-slate-300 space-y-1.5 mb-5">
                  {p.features.map(f => <li key={f} className="flex items-start gap-1.5"><Check className="w-3 h-3 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />{f}</li>)}
                </ul>
                <button onClick={() => startCheckout(p.id)} disabled={loading || isCurrent}
                  data-testid={`subscribe-${p.id}`}
                  className={`w-full text-center rounded-full py-2.5 text-sm font-medium transition-all ${isCurrent ? 'bg-gray-100 dark:bg-white/[0.05] text-gray-400 dark:text-slate-500 cursor-not-allowed' : p.popular ? 'bg-blue-600 hover:bg-blue-500 dark:hover:bg-blue-400 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'}`}>
                  {isCurrent ? 'Current plan' : loading ? 'Redirecting…' : (isTrial ? 'Subscribe' : 'Switch to this plan')}
                </button>
              </div>
            );
          })}
        </div>

        {/* Enterprise card */}
        <div data-testid="enterprise-card" className="mt-4 bg-gradient-to-r from-gray-900 to-gray-800 dark:from-white/[0.06] dark:to-white/[0.03] dark:border dark:border-white/10 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-white font-heading font-semibold text-lg">Enterprise</span>
              <span className="text-[10px] uppercase font-medium tracking-wide bg-white/20 dark:bg-white/15 text-white px-2 py-0.5 rounded-full">Custom pricing</span>
            </div>
            <p className="text-gray-300 dark:text-slate-300 text-sm">Unlimited minutes · Multi location · White label · Dedicated manager · SLA</p>
          </div>
          <a
            href="mailto:sales@nextbell.ai?subject=Enterprise%20Inquiry"
            data-testid="enterprise-contact-btn"
            className="bg-white hover:bg-gray-100 text-gray-900 text-sm font-medium rounded-full px-5 py-2.5 whitespace-nowrap flex-shrink-0"
          >
            Contact Sales →
          </a>
        </div>
      </div>

      {/* Extra-minutes flat-fee pack removed — overage and top-ups are handled by
          Pay As You Go ($0.45/min) on the Credits page, so there is one clear
          pricing story. */}

      {error && <div className="bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>}
      {refundResult && <div className="bg-green-50 dark:bg-green-500/15 border border-green-200 dark:border-green-500/30 rounded-xl px-4 py-3 text-sm text-green-700 dark:text-green-300" data-testid="refund-success">{refundResult}</div>}

      {/* Self-serve refund (only when eligible: paid plan, 0 minutes used, < 7 days) */}
      {hasStripeCustomer && !isTrial && refundEligible && (
        <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-amber-200 dark:border-amber-500/40 rounded-2xl p-6" data-testid="refund-card">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h2 className="font-heading text-lg font-medium text-amber-800 dark:text-amber-200">Request a refund</h2>
          </div>
          <p className="text-gray-600 dark:text-slate-300 text-sm mb-4">
            You haven't used any minutes yet and you're within the {refundDaysLeft}-day refund window. We'll fully refund your last payment and cancel your subscription, no questions asked.
          </p>
          {!showConfirm ? (
            <button onClick={() => setShowConfirm(true)} disabled={refundLoading}
              data-testid="refund-request-btn"
              className="bg-amber-50 dark:bg-amber-500/15 hover:bg-amber-100 text-amber-800 border border-amber-200 px-4 py-2 rounded-full text-sm font-medium">
              Request refund
            </button>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button onClick={requestRefund} disabled={refundLoading}
                data-testid="refund-confirm-btn"
                className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-4 py-2 rounded-full text-sm font-medium">
                {refundLoading ? 'Processing…' : 'Yes, refund and cancel'}
              </button>
              <button onClick={() => setShowConfirm(false)} disabled={refundLoading}
                className="bg-gray-100 dark:bg-white/[0.05] hover:bg-gray-200 text-gray-700 dark:text-slate-200 px-4 py-2 rounded-full text-sm font-medium">
                Never mind
              </button>
            </div>
          )}
        </div>
      )}
      {hasStripeCustomer && !isTrial && refundEligible === false && refundReason && (
        <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/10 rounded-2xl p-5 text-xs text-gray-500 dark:text-slate-400" data-testid="refund-ineligible">
          <strong className="text-gray-700 dark:text-slate-200">Refund unavailable:</strong> {refundReason}
        </div>
      )}

      {/* Cancel zone */}
      {hasStripeCustomer && !isTrial && (
        <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-red-200 dark:border-red-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h2 className="font-heading text-lg font-medium text-red-700 dark:text-red-300">Cancel subscription</h2>
          </div>
          <p className="text-gray-500 dark:text-slate-400 text-sm mb-4">Manage cancellation through the Stripe portal so you keep access until the end of your paid period.</p>
          <button onClick={openPortal} disabled={loading}
            className="flex items-center gap-2 bg-red-50 dark:bg-red-500/15 hover:bg-red-100 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30 px-4 py-2 rounded-full text-sm font-medium">
            <Trash2 className="w-4 h-4" /> Open Stripe portal
          </button>
        </div>
      )}
    </div>
  );
}

function Usage({ label, used, cap }) {
  const pct = cap ? Math.min((used / cap) * 100, 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600 dark:text-slate-300">{label}</span>
        <span className="text-gray-900 dark:text-white font-medium">{used} / {cap}</span>
      </div>
      <div className="w-full h-2.5 bg-gray-100 dark:bg-white/[0.05] rounded-full">
        <div className={`h-full rounded-full ${pct >= 95 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-blue-600'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

